/**
 * Utility functions for localStorage operations with error handling
 */
import { logger } from "./logger";

/**
 * Safely set an item in localStorage with error handling
 * @param key - The key to store the data under
 * @param value - The value to store (will be JSON.stringify'd)
 * @param fallback - Optional fallback function to call if storage fails
 */
export const safeSetItem = (
  key: string,
  value: any,
  fallback?: () => void
): boolean => {
  try {
    const stringValue = JSON.stringify(value);

    // Check if the data is too large (localStorage typically has 5-10MB limit)
    if (stringValue.length > 4 * 1024 * 1024) {
      // 4MB limit
      logger.warn(
        `Data for key "${key}" is too large (${(
          stringValue.length /
          1024 /
          1024
        ).toFixed(2)}MB). Skipping localStorage storage.`
      );
      if (fallback) fallback();
      return false;
    }

    localStorage.setItem(key, stringValue);
    return true;
  } catch (error) {
    logger.warn(
      `Failed to store data for key "${key}" in localStorage:`,
      error
    );
    if (fallback) fallback();
    return false;
  }
};

/**
 * Safely get an item from localStorage with error handling
 * @param key - The key to retrieve
 * @param defaultValue - Default value to return if retrieval fails
 */
export const safeGetItem = <T>(key: string, defaultValue?: T): T | null => {
  try {
    const item = localStorage.getItem(key);
    if (item === null) return defaultValue || null;
    return JSON.parse(item);
  } catch (error) {
    logger.warn(
      `Failed to retrieve data for key "${key}" from localStorage:`,
      error
    );
    return defaultValue || null;
  }
};

/**
 * Clear localStorage and handle errors gracefully
 */
export const safeClearStorage = (): boolean => {
  try {
    localStorage.clear();
    return true;
  } catch (error) {
    logger.warn("Failed to clear localStorage:", error);
    return false;
  }
};

/**
 * Remove a specific item from localStorage with error handling
 * @param key - The key to remove
 */
export const safeRemoveItem = (key: string): boolean => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    logger.warn(`Failed to remove key "${key}" from localStorage:`, error);
    return false;
  }
};
